import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import MobileNav from "../../components/mobileNav/MobileNav";
import { fetchUserPermissions } from "../../reduxSlices/userSlice";
import ImageModal from "./ImageModal";
import { useReactToPrint } from "react-to-print";
import ReactPaginate from "react-paginate";
import PrintingHeader from "../printingHeader/PrintingHeader";
import printIcon from "../../assets/print-icon.svg";
import SearchInput from "../searchBar/SearchBar";
import { useMediaQuery } from "react-responsive";
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaSnowflake,
  FaFileInvoice,
  FaCheck,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import "./newAppointment.css";

const NewAppointment = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectionPopup, setShowRejectionPopup] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [notePopUpRequestId, setNotePopUpRequestId] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imageSrc, setImageSrc] = useState("");
  const [warrantyDetailsPopup, setWarrantyDetailsPopup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    MaintenanceTypeId: "",
    WarrantyStatusId: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const itemsPerPageOptions = totalItems;

  const isDesktop = useMediaQuery({ minWidth: 767 });
  const maxLength = 9;
  const url = `https://api.generalhome.sa/api/maintenance/GetPendingRequests`;

  const { userPermissions, loadingPermissions } = useSelector(
    (state) => state.users
  );

  // Helper function to remove empty values from object
  const removeEmptyValues = (obj) => {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
      if (
        cleaned[key] === "" ||
        cleaned[key] === null ||
        cleaned[key] === undefined ||
        (Array.isArray(cleaned[key]) && cleaned[key].length === 0)
      ) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Helper function to build search parameters
  const buildSearchParams = (term) => {
    if (!term.trim()) return {};

    // Check if the search term is a phone number (contains only digits, +, or spaces)
    const isPhoneNumber = /^[\d+\s]+$/.test(term);

    // Check if the search term is a name (contains Arabic/English letters)
    const isName = /[\u0600-\u06FFa-zA-Z]/.test(term);

    const searchParams = {};

    if (isPhoneNumber) {
      // If it looks like a phone number, search only by phone
      searchParams.PhoneNumber = term.replace(/\s/g, "");
    } else if (isName) {
      // If it looks like a name, search only by name
      searchParams.CustomerName = term;
    } else {
      // For mixed or unclear input, search by both but let backend handle it
      searchParams.CustomerName = term;
      searchParams.PhoneNumber = term;
    }

    return searchParams;
  };

  const getAllAppointments = async (
    currentPage = 1,
    pageSize = itemsPerPageOptions,
    searchParams = {},
    filterParams = {}
  ) => {
    try {
      setLoading(true);

      const technicianId = localStorage.getItem("UserId");
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);

      // Step 1️⃣: Initial payload to get total items count
      const initialPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: currentPage,
        PageSize: pageSize,
        ...searchParams,
        ...filterParams,
      });

      const firstRes = await axios.post(url, initialPayload);
      const totalItemsFromAPI = firstRes.data.TotalItems || 0;

      // Step 2️⃣: If there are more items than the default page size, fetch them all
      const fullPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: 1, // always first page when showing all items
        PageSize: totalItemsFromAPI > 0 ? totalItemsFromAPI : pageSize,
        ...searchParams,
        ...filterParams,
      });

      const res =
        totalItemsFromAPI > pageSize
          ? await axios.post(url, fullPayload)
          : firstRes;

      // Handle API response
      const totalPages = res.data.TotalPages || 1;
      const totalNumberOfPages =
        totalPages ||
        Math.ceil((res.data.TotalItems || 0) / fullPayload.PageSize);

      setPageCount(totalNumberOfPages);
      setDisplayData(res.data.Items);
      setTotalItems(res.data.TotalItems);
      setCurrentPage(0);
    } catch (err) {
      setError(err.message);
      setDisplayData([]);
      setTotalItems(0);
      setPageCount(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  // Handle search submit (when user clicks search button or presses enter)
  const handleSearchSubmit = () => {
    const searchParams = buildSearchParams(searchTerm);
    getAllAppointments(1, itemsPerPage, searchParams, filters);
  };

  // Handle enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Remove item from display data after successful action
  const removeItemFromDisplayData = (requestId) => {
    setDisplayData((prevData) =>
      prevData.filter((item) => item.RequestId !== requestId)
    );
    setTotalItems((prevTotal) => prevTotal - 1);
  };

  const handleMoveDataToWaiting = async (item, condition) => {
    try {
      const parsedData = JSON.parse(JSON.stringify(item));
      const requestId = parsedData.RequestId;
      const seconed_url = `https://api.generalhome.sa/api/maintenance/ProcessMaintenanceApproveOrReject`;
      await axios.post(seconed_url, {
        RequestId: requestId,
        ProcessingTypeId: condition,
      });
      toast.success("تم قبول الطلب بنجاح", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      // Remove the item from display data instead of refetching
      removeItemFromDisplayData(requestId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMoveDataToRejected = async (item, condition) => {
    if (!rejectionReason) {
      toast.error("يرجى كتابه سبب الرفض", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }
    try {
      const third_url = `https://api.generalhome.sa/api/maintenance/ProcessMaintenanceApproveOrReject`;
      await axios.post(third_url, {
        RequestId: selectedRequestId,
        ProcessingTypeId: condition,
        ReasonRejecte: rejectionReason,
      });
      toast.success("تم رفض الطلب ", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      // Remove the item from display data instead of refetching
      removeItemFromDisplayData(selectedRequestId);
      setShowRejectionPopup(false);
      setRejectionReason("");
      setSelectedRequestId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenRejectionPopup = (item) => {
    setSelectedRequestId(item.RequestId);
    setRejectionReason("");
    setShowRejectionPopup(true);
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Reset to first page when applying filters
    getAllAppointments(1, itemsPerPage, buildSearchParams(searchTerm), filters);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      MaintenanceTypeId: "",
      WarrantyStatusId: "",
    });
    setSearchTerm(""); // Clear search term when resetting filters
    // Reset to first page and fetch without filters
    getAllAppointments(1, itemsPerPage);
  };

  // Pagination click
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    getAllAppointments(
      pageNumber,
      itemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    getAllAppointments(
      1,
      newItemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const handleTogglePopup = (item) => {
    if (notePopUpRequestId === item.RequestId) {
      setNotePopUpRequestId(null);
    } else {
      setNotePopUpRequestId(item.RequestId);
    }
  };

  const handleImageClick = (src) => {
    setImageSrc(src);
    setIsImageModalOpen(true);
  };

  const handleZoomIn = () => {
    setZoomLevel(zoomLevel + 0.1);
  };

  const handleZoomOut = () => {
    setZoomLevel(zoomLevel - 0.1);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setZoomLevel(1);
  };

  const handleShowWarrantyDetails = (maintenanceDetails) => {
    setWarrantyDetailsPopup(maintenanceDetails);
  };

  const handleCloseWarrantyDetails = () => {
    setWarrantyDetailsPopup(null);
  };

  const formatACTypes = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    return maintenanceDetails.map((detail) => detail.ACTypeName).join("، ");
  };

  const getWarrantyStatus = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";

    const firstStatus = maintenanceDetails[0].WarrantyStatusName;
    const allSame = maintenanceDetails.every(
      (item) => item.WarrantyStatusName === firstStatus
    );

    return allSame ? firstStatus : "مختلط";
  };

  // Render pagination
  const renderPagination = () => (
    <ReactPaginate
      forcePage={currentPage}
      previousLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.94 4L6 4.94L9.05333 8L6 11.06L6.94 12L10.94 8L6.94 4Z"
            fill="black"
          />
        </svg>
      }
      nextLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.06 12L11 11.06L7.94667 8L11 4.94L10.06 4L6.06 8L10.06 12Z"
            fill="black"
            fillOpacity="0.3"
          />
        </svg>
      }
      breakLabel={"..."}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={3}
      onPageChange={handlePageClick}
      containerClassName={"pagination justify-content-end"}
      pageClassName={"page-item"}
      pageLinkClassName={"page-link"}
      previousClassName={"page-item"}
      previousLinkClassName={"page-link"}
      nextClassName={"page-item"}
      nextLinkClassName={"page-link"}
      breakClassName={"page-item"}
      breakLinkClassName={"break-link"}
      activeClassName={"active"}
    />
  );

  useEffect(() => {
    const userId = localStorage.getItem("UserId");
    if (userId) {
      dispatch(fetchUserPermissions(userId));
    } else {
      console.error("User ID not found in local storage");
    }

    getAllAppointments(1, itemsPerPage);
  }, [dispatch]);

  const canWritePendingRequests =
    userPermissions?.CanWritePendingRequests === "true";

  return (
    <>
      <MobileNav />
      <section className="newAppointment mt-0">
        <Helmet>
          <meta charSet="utf-8" />
          <title>طلبات الصيانه الجديده</title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/newAppointment"
          />
        </Helmet>
        {loading || loadingPermissions ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading || loadingPermissions}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : (
          <>
            {isDesktop ? (
              <div className="newAppointment-container" ref={componentRef}>
                <PrintingHeader
                  title="طلبات الصيانه الجديده"
                  count={totalItems}
                />

                <SearchInput
                  searchTerm={searchTerm}
                  handleSearch={handleSearchChange}
                  handleSearchSubmit={handleSearchSubmit}
                  handleKeyPress={handleKeyPress}
                  setSearchTerm={setSearchTerm}
                  placeholder="ابحث عن اسم العميل أو رقم الجوال"
                />

                <div className="d-flex justify-content-between info">
                  <div className="info-header">
                    <h2>طلبات الصيانه الجديده</h2>
                    <h3>{totalItems}</h3>
                  </div>
                  <div className="print-icon-holder">
                    <img
                      src={printIcon}
                      alt="printIcon"
                      className="print-icon"
                      onClick={handlePrint}
                    />
                  </div>
                </div>

                {/* Filter form */}
                <div className="form-holder">
                  <h5>ما الذي تبحث عنه؟</h5>
                  <form
                    className="mt-4 filter-form"
                    onSubmit={handleFilterSubmit}
                  >
                    <div className="filter-row">
                      <div className="filter-group">
                        <select
                          name="MaintenanceTypeId"
                          value={filters.MaintenanceTypeId}
                          onChange={handleFilterChange}
                        >
                          <option value="">اختر نوع الخدمة</option>
                          <option value="1">صيانه</option>
                          <option value="2">تنظيف</option>
                        </select>
                      </div>
                      <div className="filter-group">
                        <select
                          name="WarrantyStatusId"
                          value={filters.WarrantyStatusId}
                          onChange={handleFilterChange}
                        >
                          <option value="">اختر حالة الضمان</option>
                          <option value="1">داخل الضمان</option>
                          <option value="2">خارج الضمان</option>
                        </select>
                      </div>
                    </div>
                    <div className="filter-button-container">
                      <button
                        type="button"
                        className="reset-button"
                        onClick={handleResetFilters}
                        disabled={loading}
                      >
                        إعادة تعيين
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? "جاري التصفية..." : "تصفية"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Conditionally render table or no data card */}
                {displayData?.length > 0 ? (
                  <div className="table" ref={componentRef}>
                    <div className="table-responsive-sm overflow-auto">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th style={{ width: "90px" }}>نوع الطلب</th>
                            <th>إسم العميل</th>
                            <th>رقم الهاتف</th>
                            <th>المنطقه</th>
                            <th>المدينه</th>
                            <th>المكيف</th>
                            <th>الملاحظات</th>
                            <th className="text-center">الفاتوره</th>
                            <th className="text-center">الضمان</th>
                            <th className="text-center">التحكم</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayData.map((item) => {
                            const warrantyStatus = getWarrantyStatus(
                              item.MaintenanceRequestDetails
                            );
                            return (
                              <tr key={item.RequestId}>
                                <td>
                                  {item.MaintenanceTypeId === 1 ? (
                                    <p className="maintainance">صيانه</p>
                                  ) : (
                                    <p className="clean">تنظيف</p>
                                  )}
                                </td>
                                <td>
                                  {item.CustomerName?.length > maxLength
                                    ? `${item.CustomerName.substring(
                                        0,
                                        maxLength
                                      )}...`
                                    : item.CustomerName}
                                </td>
                                <td>
                                  <a
                                    href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                                      /[^\d+]/g,
                                      ""
                                    )}&text=مرحباً، أود الإستفسار عن الطلب`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item.PhoneNumber}
                                  </a>
                                </td>
                                <td>{item.Region}</td>
                                <td>{item.City}</td>
                                <td>
                                  {formatACTypes(item.MaintenanceRequestDetails)
                                    ?.length > maxLength
                                    ? `${formatACTypes(
                                        item.MaintenanceRequestDetails
                                      ).substring(0, maxLength)}...`
                                    : formatACTypes(
                                        item.MaintenanceRequestDetails
                                      )}
                                </td>
                                <td>
                                  {item.ErrorType?.trim()
                                    ? item.ErrorType.length > maxLength
                                      ? `${item.ErrorType.substring(
                                          0,
                                          maxLength
                                        )}...`
                                      : item.ErrorType
                                    : "No note"}

                                  {item.ErrorType?.trim() &&
                                    item.ErrorType.length > maxLength && (
                                      <span
                                        onClick={() => handleTogglePopup(item)}
                                        className="see-more-link"
                                      >
                                        المزيد
                                      </span>
                                    )}

                                  {item.ErrorType?.trim() &&
                                    notePopUpRequestId === item.RequestId && (
                                      <div className="notePopup">
                                        <div className="notePopup-content">
                                          <button
                                            onClick={() =>
                                              handleTogglePopup(item)
                                            }
                                            className="close-btn d-flex"
                                          >
                                            X
                                          </button>
                                          <h3>الملاحظات</h3>
                                          <p>{item.ErrorType}</p>
                                        </div>
                                      </div>
                                    )}
                                </td>

                                <td>
                                  {item.ImagePath &&
                                  item.ImagePath !==
                                    "https://api.generalhome.sa/Resources/Images/" ? (
                                    <img
                                      src={item.ImagePath}
                                      alt="Receipt"
                                      onClick={() =>
                                        handleImageClick(item.ImagePath)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        maxWidth: "150px",
                                      }}
                                    />
                                  ) : (
                                    <p className="text-center">No Image</p>
                                  )}
                                </td>
                                <td
                                  className={
                                    warrantyStatus === "داخل الضمان"
                                      ? "warranty-inside"
                                      : "warranty-outside"
                                  }
                                >
                                  {warrantyStatus === "مختلط" ? (
                                    <span
                                      className="mixed-warranty"
                                      onClick={() =>
                                        handleShowWarrantyDetails(
                                          item.MaintenanceRequestDetails
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      مختلط
                                    </span>
                                  ) : (
                                    <p>{warrantyStatus}</p>
                                  )}
                                </td>
                                <td className="waitingBtn">
                                  <div
                                    className="btn-holder text-center"
                                    // style={{ width: "max-content" }}
                                  >
                                    {/* <div className="topButtons"> */}
                                    <button
                                      onClick={() =>
                                        handleMoveDataToWaiting(item, 2)
                                      }
                                      className="btn btn-approve"
                                      disabled={!canWritePendingRequests}
                                    >
                                      قبول
                                    </button>
                                    <button
                                      className="btn btn-danger"
                                      onClick={() =>
                                        handleOpenRejectionPopup(item)
                                      }
                                      disabled={!canWritePendingRequests}
                                    >
                                      رفض
                                    </button>
                                    {/* </div> */}

                                    {canWritePendingRequests &&
                                      showRejectionPopup &&
                                      selectedRequestId === item.RequestId && (
                                        <div className="rejection-popup">
                                          <h4>سبب الرفض</h4>
                                          <div className="rejection-popup-container">
                                            <textarea
                                              value={rejectionReason}
                                              onChange={(e) =>
                                                setRejectionReason(
                                                  e.target.value
                                                )
                                              }
                                              placeholder="أكتب سبب الرفض هنا"
                                              required
                                            />
                                            <div className="popup-actions">
                                              <button
                                                className="btn btn-danger"
                                                onClick={() =>
                                                  setShowRejectionPopup(false)
                                                }
                                              >
                                                إلغاء
                                              </button>
                                              <button
                                                onClick={() =>
                                                  handleMoveDataToRejected(
                                                    item,
                                                    3
                                                  )
                                                }
                                              >
                                                تأكيد
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    {/* <div className="bottomBtn"> */}
                                    <Link
                                      className="link"
                                      to={`/dashboard/inProgress/${item.RequestId}`}
                                    >
                                      <button>تفاصيل</button>
                                    </Link>
                                    {/* </div> */}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="no-data-card">
                    <div className="no-data-content">
                      <div className="no-data-icon">📭</div>
                      <h3>لا توجد طلبات جديدة</h3>
                      <p>لا توجد بيانات لعرضها حالياً</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Mobile View
              <div className="newAppointmentMob-container" ref={componentRef}>
                <PrintingHeader
                  title="طلبات الصيانه الجديده"
                  count={totalItems}
                />

                <SearchInput
                  searchTerm={searchTerm}
                  handleSearch={handleSearchChange}
                  handleSearchSubmit={handleSearchSubmit}
                  handleKeyPress={handleKeyPress}
                  setSearchTerm={setSearchTerm}
                  placeholder="ابحث عن اسم العميل أو رقم الجوال"
                />

                <div className="d-flex justify-content-between info">
                  <div className="info-header">
                    <h2>طلبات الصيانه الجديده</h2>
                    <h3>{totalItems}</h3>
                  </div>
                  <div className="print-icon-holder">
                    <img
                      src={printIcon}
                      alt="printIcon"
                      className="print-icon"
                      onClick={handlePrint}
                    />
                  </div>
                </div>

                {/* Mobile filter form */}
                <div className="form-holder">
                  <h5>ما الذي تبحث عنه؟</h5>
                  <form
                    className="mt-4 filter-form"
                    onSubmit={handleFilterSubmit}
                  >
                    <div className="filter-group">
                      <select
                        name="MaintenanceTypeId"
                        value={filters.MaintenanceTypeId}
                        onChange={handleFilterChange}
                      >
                        <option value="">اختر نوع الخدمة</option>
                        <option value="1">صيانه</option>
                        <option value="2">تنظيف</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <select
                        name="WarrantyStatusId"
                        value={filters.WarrantyStatusId}
                        onChange={handleFilterChange}
                      >
                        <option value="">اختر حالة الضمان</option>
                        <option value="1">داخل الضمان</option>
                        <option value="2">خارج الضمان</option>
                      </select>
                    </div>
                    <div className="filter-button-container">
                      <button
                        type="button"
                        className="reset-button"
                        onClick={handleResetFilters}
                        disabled={loading}
                      >
                        إعادة تعيين
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? "جاري التصفية..." : "تصفية"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Cards Container */}
                <div className="beautiful-cards-container">
                  {displayData?.length > 0 ? (
                    displayData.map((item) => {
                      const warrantyStatus = getWarrantyStatus(
                        item.MaintenanceRequestDetails
                      );

                      return (
                        <div
                          key={item.RequestId}
                          className={`beautiful-mob-card ${
                            item.MaintenanceTypeId === 2
                              ? "cleaning"
                              : "maintenance"
                          }`}
                        >
                          {/* Card Header */}
                          <div className="card-top-bar">
                            <div className="service-badge">
                              {item.MaintenanceTypeId === 1 ? "صيانه" : "تنظيف"}
                            </div>
                            <div className="warranty-status">
                              {warrantyStatus === "مختلط" ? (
                                <span
                                  className="mixed-warranty"
                                  onClick={() =>
                                    handleShowWarrantyDetails(
                                      item.MaintenanceRequestDetails
                                    )
                                  }
                                >
                                  {warrantyStatus}
                                </span>
                              ) : (
                                <span
                                  className={`warranty-tag ${
                                    warrantyStatus === "داخل الضمان"
                                      ? "inside"
                                      : "outside"
                                  }`}
                                >
                                  {warrantyStatus}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Customer Information */}
                          <div className="card-section customer-section">
                            <div className="section-header">
                              <span>معلومات العميل</span>
                            </div>
                            <div className="customer-info">
                              <div className="info-item">
                                <span className="info-label">الاسم:</span>
                                <span className="info-value highlight">
                                  {item.CustomerName}
                                </span>
                              </div>
                              <div className="info-item">
                                <span className="info-label">الهاتف:</span>
                                <a
                                  href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                                    /[^\d+]/g,
                                    ""
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="whatsapp-link"
                                >
                                  <FaWhatsapp className="whatsapp-icon" />
                                  {item.PhoneNumber}
                                </a>
                              </div>
                            </div>
                          </div>

                          {/* Location Information */}
                          <div className="card-section location-section">
                            <div className="section-header">
                              <FaMapMarkerAlt className="section-icon" />
                              <span>الموقع</span>
                            </div>
                            <div className="location-info">
                              <div className="info-item">
                                <span className="info-label">المنطقة:</span>
                                <span className="info-value">
                                  {item.Region}
                                </span>
                              </div>
                              <div className="info-item">
                                <span className="info-label">المدينة:</span>
                                <span className="info-value">{item.City}</span>
                              </div>
                            </div>
                          </div>

                          {/* AC Information */}
                          <div className="card-section ac-section">
                            <div className="section-header">
                              <FaSnowflake className="section-icon" />
                              <span>المكيف</span>
                            </div>
                            <div className="ac-info">
                              <span className="ac-types">
                                {formatACTypes(item.MaintenanceRequestDetails)}
                              </span>
                            </div>
                          </div>

                          {/* Notes Section */}
                          <div className="card-section notes-section">
                            <div className="section-header">
                              <FaInfoCircle className="section-icon" />
                              <span>الملاحظات</span>
                            </div>
                            <div className="notes-content">
                              {item.ErrorType}
                            </div>
                          </div>

                          {/* Receipt Image */}
                          <div className="card-section receipt-section">
                            <div className="section-header">
                              <FaFileInvoice className="section-icon" />
                              <span>الفاتورة</span>
                            </div>
                            <div className="receipt-image">
                              {item.ImagePath &&
                              item.ImagePath !==
                                "https://api.generalhome.sa/Resources/Images/" ? (
                                <img
                                  src={item.ImagePath}
                                  alt="Receipt"
                                  onClick={() =>
                                    handleImageClick(item.ImagePath)
                                  }
                                  className="receipt-img"
                                />
                              ) : (
                                <span className="no-image">لا توجد صورة</span>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="card-actions">
                            <div className="action-buttons-grid">
                              <button
                                onClick={() => handleMoveDataToWaiting(item, 2)}
                                disabled={!canWritePendingRequests}
                                className="action-btn approve-btn"
                              >
                                <FaCheck />
                                قبول
                              </button>

                              <button
                                onClick={() => handleOpenRejectionPopup(item)}
                                disabled={!canWritePendingRequests}
                                className="action-btn reject-btn"
                              >
                                <FaTimes />
                                رفض
                              </button>

                              <Link
                                to={`/dashboard/inProgress/${item.RequestId}`}
                                className="action-link"
                              >
                                <button className="action-btn details-btn">
                                  <FaInfoCircle />
                                  تفاصيل
                                </button>
                              </Link>
                            </div>

                            {canWritePendingRequests &&
                              showRejectionPopup &&
                              selectedRequestId === item.RequestId && (
                                <div className="rejection-popup">
                                  <h4>سبب الرفض</h4>
                                  <div className="rejection-popup-content">
                                    <textarea
                                      value={rejectionReason}
                                      onChange={(e) =>
                                        setRejectionReason(e.target.value)
                                      }
                                      placeholder="أكتب سبب الرفض هنا"
                                      required
                                      className="rejection-textarea"
                                    />
                                    <div className="popup-actions">
                                      <button
                                        onClick={() =>
                                          setShowRejectionPopup(false)
                                        }
                                        className="popup-btn btn btn-danger"
                                      >
                                        إلغاء
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleMoveDataToRejected(item, 3)
                                        }
                                        className="mob-popup-confirmbtn"
                                      >
                                        تأكيد
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-data-card">
                      <div className="no-data-content">
                        <div className="no-data-icon">📭</div>
                        <h3>لا توجد طلبات جديدة</h3>
                        <p>لا توجد بيانات لعرضها حالياً</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {isImageModalOpen && (
          <ImageModal
            imageSrc={imageSrc}
            zoomLevel={zoomLevel}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            handleCloseImageModal={handleCloseImageModal}
            setZoomLevel={setZoomLevel}
          />
        )}

        {warrantyDetailsPopup && (
          <div className="warranty-details-popup">
            <div className="warranty-details-card">
              <div className="warranty-details-header">
                <h3>تفاصيل حالة الضمان</h3>
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-btn"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="warranty-details-body">
                <div className="warranty-status-summary">
                  <span className="summary-label">الحالة الإجمالية:</span>
                  <span
                    className={`summary-value ${
                      getWarrantyStatus(warrantyDetailsPopup) === "داخل الضمان"
                        ? "inside"
                        : "outside"
                    }`}
                  >
                    {getWarrantyStatus(warrantyDetailsPopup)}
                  </span>
                </div>
                <div className="warranty-items-container">
                  {warrantyDetailsPopup.map((item, index) => (
                    <div key={index} className="warranty-item">
                      <div className="ac-type">
                        <i className="fas fa-snowflake"></i>
                        <div>{item.ACTypeCount}</div>
                        <span>{item.ACTypeName}</span>
                      </div>
                      <div
                        className={`warranty-status ${
                          item.WarrantyStatusName === "داخل الضمان"
                            ? "inside"
                            : "outside"
                        }`}
                      >
                        {item.WarrantyStatusName}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="warranty-details-footer">
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-button"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default NewAppointment;
